// Background EA-extraction pipeline state — SERVER-BACKED. ea-manager records
// every upload (ListUploads / DismissUpload), so extraction progress follows
// the ACCOUNT: any session or device sees in-flight and finished uploads.
// This store is only an in-memory mirror of the server rows; the header
// indicator hydrates and polls it, the Fleet strip renders it, and the deploy
// flow resumes from it (?artifact=<sha> — ready entries carry the schema JSON
// so no extra round-trip is needed).

import { create } from 'zustand';
import { UploadEntry } from 'proto/ea-hosting_pb';

export type ExtractionStatus = 'extracting' | 'ready' | 'failed';

export type ExtractionJob = {
  sha256: string;
  artifactId: string;
  fileName: string;
  platform: string;
  status: ExtractionStatus;
  schemaJson?: string;
  inputCount?: number;
  error?: string;
  createdAtSec: number;
};

type ExtractionState = {
  jobs: Record<string, ExtractionJob>;
  hydrated: boolean;
  // Session-local ack: which terminal jobs the badge has already counted.
  // Removal-for-everyone is the server-side dismiss; this only quiets the
  // badge in the current tab after the user has looked at the list.
  seen: Record<string, true>;
  setFromServer: (entries: UploadEntry.AsObject[]) => void;
  // Optimistic local registration right after UploadEA returns, so the
  // indicator appears instantly (the next server poll confirms it).
  registerLocal: (job: Omit<ExtractionJob, 'status' | 'createdAtSec'>) => void;
  markSeen: (sha256: string) => void;
  removeLocal: (sha256: string) => void;
};

const statusOf = (s: string): ExtractionStatus => (s === 'ready' ? 'ready' : s === 'failed' ? 'failed' : 'extracting');

export const useExtractionStore = create<ExtractionState>()((set) => ({
  jobs: {},
  hydrated: false,
  seen: {},
  setFromServer: (entries) =>
    set(() => {
      const jobs: Record<string, ExtractionJob> = {};
      entries.forEach((e) => {
        jobs[e.sha256] = {
          sha256: e.sha256,
          artifactId: e.eaArtifactId,
          fileName: e.fileName,
          platform: e.platform,
          status: statusOf(e.status),
          schemaJson: e.schemaJson || undefined,
          inputCount: e.inputCount || undefined,
          error: e.error || undefined,
          createdAtSec: e.createdAt?.seconds || 0
        };
      });
      return { jobs, hydrated: true };
    }),
  registerLocal: (job) =>
    set((s) => ({
      jobs: {
        ...s.jobs,
        [job.sha256]: { ...job, status: 'extracting', createdAtSec: Math.floor(Date.now() / 1000) }
      }
    })),
  markSeen: (sha256) => set((s) => ({ seen: { ...s.seen, [sha256]: true } })),
  removeLocal: (sha256) =>
    set((s) => {
      const jobs = { ...s.jobs };
      delete jobs[sha256];
      return { jobs };
    })
}));

export const selectUnseenDone = (s: ExtractionState) => Object.values(s.jobs).filter((j) => j.status !== 'extracting' && !s.seen[j.sha256]);
export const selectVisibleJobs = (s: ExtractionState) => Object.values(s.jobs).sort((a, b) => b.createdAtSec - a.createdAtSec);
